// Thin wrapper over @learnforge/i18n. Scenes import the same names they used
// before the migration; under the hood every call delegates to a single
// `createI18n` instance.
//
// Language preference is persisted into luminary's settings blob (alongside
// hintsEnabled etc.) so existing players keep their preferred locale across
// the upgrade.

import { createI18n, LANGUAGES, type I18n, type LangStorage } from '@learnforge/i18n';
import { progressStore } from '../progress';
import { ar } from './locales/ar';
import { bn } from './locales/bn';
import { de } from './locales/de';
import { en } from './locales/en';
import { es } from './locales/es';
import { fa } from './locales/fa';
import { fr } from './locales/fr';
import { gu } from './locales/gu';
import { hi } from './locales/hi';
import { id } from './locales/id';
import { it } from './locales/it';
import { ja } from './locales/ja';
import { ko } from './locales/ko';
import { mr } from './locales/mr';
import { nl } from './locales/nl';
import { pa } from './locales/pa';
import { pl } from './locales/pl';
import { pt } from './locales/pt';
import { ro } from './locales/ro';
import { ru } from './locales/ru';
import { sw } from './locales/sw';
import { ta } from './locales/ta';
import { te } from './locales/te';
import { th } from './locales/th';
import { tl } from './locales/tl';
import { tr } from './locales/tr';
import { uk } from './locales/uk';
import { ur } from './locales/ur';
import { vi } from './locales/vi';
import { zh } from './locales/zh';
import type { Direction, GameKey, LangCode, LanguageEntry, TranslationKey } from './types';

// Language sits inside luminary's shared settings blob (preserving the pre-
// migration storage shape). The adapter is a tiny lens over progressStore.
// IMPORTANT: do not reference the local `instance` here — load() is invoked
// during createI18n's constructor, *before* `instance` has been assigned.
const langStorage: LangStorage = {
  load(): LangCode | null {
    const v = progressStore.setting<unknown>('language', null);
    return typeof v === 'string' && isKnownCode(v) ? v : null;
  },
  save(code: LangCode): void {
    progressStore.setSetting('language', code);
  },
};

function isKnownCode(value: string): value is LangCode {
  return LANGUAGES.some((l) => l.code === value);
}

const PER_GAME_LOCALES = {
  en,
  ar,
  bn,
  de,
  es,
  fa,
  fr,
  gu,
  hi,
  id,
  it,
  ja,
  ko,
  mr,
  nl,
  pa,
  pl,
  pt,
  ro,
  ru,
  sw,
  ta,
  te,
  th,
  tl,
  tr,
  uk,
  ur,
  vi,
  zh,
};

let instance: I18n<GameKey> = build();

function build(): I18n<GameKey> {
  return createI18n<GameKey>({
    gameId: 'luminary',
    locales: PER_GAME_LOCALES,
    storage: langStorage,
  });
}

/**
 * Re-reads persisted language + navigator detection. Called once from main.ts
 * after locale tables load. Tests also call this between `_resetForTests` and
 * assertions to simulate a fresh boot.
 */
export function init(): void {
  instance = build();
}

export function t(key: TranslationKey, params?: Record<string, string | number>): string {
  return instance.t(key, params);
}

export function currentLang(): LangCode {
  return instance.currentLang();
}

export function isRTL(): boolean {
  return instance.isRTL();
}

export function setLang(code: LangCode): void {
  instance.setLang(code);
}

export function languages(): readonly LanguageEntry[] {
  return instance.languages() as readonly LanguageEntry[];
}

export function subscribeToLanguageChange(
  cb: (lang: LangCode, dir: Direction) => void,
): () => void {
  return instance.subscribe(cb);
}

export function _resetForTests(): void {
  instance._resetForTests();
  instance = build();
}

// Internal re-export — used by helpers.ts to avoid binding to a stale instance.
export function _getInstance(): I18n<GameKey> {
  return instance;
}
